import classNames from 'classnames'
import React from 'react'
import { useTranslation } from 'react-i18next'
import * as R from 'remeda'

import { Box } from '~/components/elements/Box'
import { Typography } from '~/components/elements/Typography'
import {
  type NineSegment,
  nineSegmentIntentionI18nKeyMap,
  nineSegments,
} from '~/utils'

import {
  NineSegmentCheckbox,
  type NineSegmentCheckboxProps,
} from './components/NineSegmentCheckbox'
import styles from './NineSegmentsCheckPanel.css'

export type NineSegmentsCheckPanelProps = {
  className?: string
  'data-testid'?: string
  onChange?: (value: NineSegment[]) => void
  value: NineSegment[]
}

export const NineSegmentsCheckPanel = React.forwardRef<
  HTMLDivElement,
  NineSegmentsCheckPanelProps
>(
  (
    {
      className,
      'data-testid': dataTestId = 'components-features-nine-segments-check-panel',
      onChange,
      value,
    },
    ref,
  ) => {
    const { t } = useTranslation()

    const handleChange = React.useCallback<
      EH<NineSegmentCheckboxProps, 'onChange'>
    >(
      (nineSegment, checked) => {
        const newValue = R.intersection(
          nineSegments,
          checked
            ? R.concat(value, [nineSegment])
            : R.difference(value, [nineSegment]),
        )
        onChange?.(newValue)
      },
      [onChange, value],
    )

    return (
      <Box
        ref={ref}
        className={classNames(styles.root, className)}
        data-testid={dataTestId}
      >
        <Typography className={styles.positiveLabel} variant="normal">
          {t(nineSegmentIntentionI18nKeyMap.nineSegment1)}
        </Typography>

        <Typography className={styles.negativeLabel} variant="normal">
          {t(nineSegmentIntentionI18nKeyMap.nineSegment2)}
        </Typography>

        {nineSegments.map((nineSegment) => (
          <NineSegmentCheckbox
            key={nineSegment}
            checked={value.includes(nineSegment)}
            className={styles[nineSegment]}
            nineSegment={nineSegment}
            onChange={handleChange}
          />
        ))}
      </Box>
    )
  },
)
